import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API_BASE_URL from '../config/api';
import { useTheme } from '../utils/ThemeContext';
import { useInteractiveTutorial } from '../utils/InteractiveTutorialContext';
import { 
  Box, 
  Container, 
  Paper, 
  TextField, 
  Button, 
  Typography, 
  FormControlLabel, 
  Checkbox, 
  Alert,
  IconButton,
  InputAdornment
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Email as EmailIcon,
  Lock as LockIcon,
  Login as LoginIcon
} from '@mui/icons-material';
import axios from 'axios';
import '../assets/css/futuristic-modern.css';

// Add custom styles for theme-aware inputs
const themeInputStyles = {
  '& .MuiOutlinedInput-root': {
    color: 'var(--text-primary) !important',
    backgroundColor: 'var(--surface) !important',
    '& fieldset': {
      borderColor: 'var(--border) !important',
    },
    '&:hover fieldset': {
      borderColor: 'var(--neon-cyan) !important',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'var(--neon-cyan) !important',
    },
    '& input': {
      color: 'var(--text-primary) !important',
      backgroundColor: 'transparent !important',
    },
    '& .MuiInputAdornment-root': {
      color: 'var(--text-muted) !important',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'var(--text-muted) !important',
    '&.Mui-focused': {
      color: 'var(--neon-cyan) !important',
    },
  },
};

const SignIn = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [requiresVerification, setRequiresVerification] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [verificationType, setVerificationType] = useState(''); // 'success' or 'error'
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { reloadTheme } = useTheme();
  const { loadTutorialStatus } = useInteractiveTutorial();

  // Check for verification status, password reset, and session expiration from URL params
  useEffect(() => {
    const verification = searchParams.get('verification');
    const reset = searchParams.get('reset');
    const message = searchParams.get('message');
    
    // Handle session expiration message
    if (message && message.includes('session has expired')) {
      setVerificationType('warning');
      setVerificationMessage(message);
      // Auto-dismiss after 7 seconds
      setTimeout(() => {
        setVerificationMessage('');
        setVerificationType('');
      }, 7000);
      // Clear URL params
      setSearchParams({});
    } else if (message && message.includes('sign in to continue')) {
      setVerificationType('info');
      setVerificationMessage(message);
      // Auto-dismiss after 5 seconds
      setTimeout(() => {
        setVerificationMessage('');
        setVerificationType('');
      }, 5000);
      // Clear URL params
      setSearchParams({});
    } else if (verification === 'success') {
      setVerificationType('success');
      setVerificationMessage('Email verified successfully! You can now sign in.');
      // Auto-dismiss after 5 seconds
      setTimeout(() => {
        setVerificationMessage('');
        setVerificationType('');
      }, 5000);
      // Clear URL params
      setSearchParams({});
    } else if (verification === 'error') {
      setVerificationType('error');
      setVerificationMessage(message || 'Verification failed. Please try again.');
      // Auto-dismiss after 7 seconds
      setTimeout(() => {
        setVerificationMessage('');
        setVerificationType('');
      }, 7000);
      // Clear URL params
      setSearchParams({});
    } else if (reset === 'success') {
      setVerificationType('success');
      setVerificationMessage('Password reset successfully! You can now sign in with your new password.');
      // Auto-dismiss after 5 seconds
      setTimeout(() => {
        setVerificationMessage('');
        setVerificationType('');
      }, 5000);
      // Clear URL params
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    setRequiresVerification(false);
    setResendSuccess(false);
    
    try {
        console.log('🔍 SignIn: Attempting to sign in with API_BASE_URL:', API_BASE_URL);
        console.log('🔍 SignIn: Full signin URL:', `${API_BASE_URL}/api/signin`);
        const response = await axios.post(`${API_BASE_URL}/api/signin`, { email, password });
        const access_token = response.data.access_token;
        const refresh_token = response.data.refresh_token;
        const profile = response.data.profile;
      
        if (access_token) {
        localStorage.setItem('authToken', access_token);
        // Always store refresh token for sliding session (30-day expiry)
        localStorage.setItem('refreshToken', refresh_token);
        
        // Reload theme from database now that user is authenticated
        console.log('🎨 SignIn: Reloading theme after authentication...');
        await reloadTheme();
        
        // Load tutorial status after successful login
        await loadTutorialStatus();
        
        // Store profile data including profileComplete status
        if (profile) {
          console.log('🔍 SignIn: Received profile data from server:', profile);
          console.log('🔍 SignIn: profileComplete value:', profile.profileComplete, 'type:', typeof profile.profileComplete);
          localStorage.setItem('userProfileData', JSON.stringify(profile));
          // Trigger storage event to notify TopNavigation of the update
          window.dispatchEvent(new Event('storage'));
        }
        
        navigate('/dashboard');
        } else {
        setErrorMessage('No token received from server');
        }
    } catch (error) {
      console.error('🔍 SignIn: Error details:', error);
      console.error('🔍 SignIn: Error response:', error.response);
      console.error('🔍 SignIn: Error request:', error.request);
      console.error('🔍 SignIn: Error message:', error.message);
      
      if (error.response) {
        console.log('🔍 SignIn: Server responded with error:', error.response.status, error.response.data);
        // Check for both 'error' and 'message' fields (backend uses 'error')
        const errorMsg = error.response.data.error || error.response.data.message || "Invalid credentials";
        setErrorMessage(errorMsg);
        
        // Check if this is an email verification error
        if (error.response.status === 403 && error.response.data.requiresVerification) {
          setRequiresVerification(true);
        }
      } else if (error.request) {
        console.log('🔍 SignIn: No response received from server');
        setErrorMessage("No response received from server. Please try again.");
      } else {
        console.log('🔍 SignIn: Unexpected error:', error.message);
        setErrorMessage("An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleResendVerification = async () => {
    if (!email) {
      setErrorMessage('Please enter your email address');
      return;
    }
    
    setResendingEmail(true);
    setResendSuccess(false);
    setErrorMessage('');
    
    try {
      await axios.post(`${API_BASE_URL}/api/resend-verification`, { email });
      setResendSuccess(true);
      setErrorMessage('Verification email sent! Please check your inbox and spam folder.');
    } catch (error) {
      setErrorMessage(error.response?.data?.error || 'Failed to resend verification email');
    } finally {
      setResendingEmail(false);
    }
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        background: 'var(--surface)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2
      }}
    >
      <Container maxWidth="sm">
        <Paper 
          elevation={24}
          sx={{
            background: 'var(--surface-2)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-lg)',
            backdropFilter: 'blur(20px)',
            padding: { xs: 3, md: 4 },
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'var(--accent-gradient)',
            }
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography 
              variant="h3" 
              component="h1" 
              sx={{ 
                color: '#468230',
                fontWeight: 700,
                mb: 1
              }}
            >
              Welcome Back
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'var(--text-primary)',
                fontSize: '1.1rem'
              }}
            >
              Sign in to access your research dashboard
            </Typography>
          </Box>

          {/* Verification Alert */}
          {verificationMessage && (
            <Alert 
              severity={verificationType === 'success' ? "success" : "error"}
              onClose={() => setVerificationMessage('')}
              sx={{ 
                mb: 3,
                background: verificationType === 'success' ? 'rgba(70, 130, 48, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                border: verificationType === 'success' ? '1px solid rgba(70, 130, 48, 0.3)' : '1px solid rgba(244, 67, 54, 0.3)',
                color: verificationType === 'success' ? '#468230' : '#ff6b6b'
              }}
            >
              {verificationMessage}
            </Alert>
          )}

          {/* Error Alert */}
          {errorMessage && (
            <Alert 
              severity={resendSuccess ? "success" : "error"}
              sx={{ 
                mb: 3,
                background: resendSuccess ? 'rgba(70, 130, 48, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                border: resendSuccess ? '1px solid rgba(70, 130, 48, 0.3)' : '1px solid rgba(244, 67, 54, 0.3)',
                color: resendSuccess ? '#468230' : '#ff6b6b'
              }}
            >
              <Box>
                {errorMessage}
                {requiresVerification && !resendSuccess && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, color: 'inherit' }}>
                      Didn't receive the email? Check your spam folder or:
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleResendVerification}
                      disabled={resendingEmail}
                      sx={{
                        color: 'inherit',
                        borderColor: 'currentColor',
                        '&:hover': {
                          borderColor: 'currentColor',
                          backgroundColor: 'rgba(255, 107, 107, 0.1)'
                        }
                      }}
                    >
                      {resendingEmail ? 'Sending...' : 'Resend Verification Email'}
                    </Button>
                  </Box>
                )}
              </Box>
            </Alert>
          )}

          {/* Sign In Form */}
          <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
                         <TextField
               fullWidth
               label="Email Address"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)} 
                          required 
               variant="outlined"
               sx={{
                 mb: 3,
                 ...themeInputStyles
               }}
               InputProps={{
                 startAdornment: (
                   <InputAdornment position="start">
                     <EmailIcon sx={{ color: 'var(--text-muted)' }} />
                   </InputAdornment>
                 ),
               }}
             />

                         <TextField
               fullWidth
               label="Password"
               type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)} 
                          required 
               variant="outlined"
               sx={{
                 mb: 3,
                 ...themeInputStyles
               }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: 'var(--text-muted)' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      sx={{ color: 'var(--text-muted)' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    sx={{
                      color: 'var(--text-muted)',
                      '&.Mui-checked': {
                        color: 'var(--neon-cyan)',
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ color: 'var(--text-primary)' }}>
                    Remember me
                  </Typography>
                }
              />
              <Link 
                to="/forgot-password"
                style={{ 
                  color: 'var(--neon-cyan)', 
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500
                }}
              >
                Forgot Password?
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              startIcon={<LoginIcon />}
              sx={{
                background: 'var(--accent-gradient)',
                color: '#ffffff !important',
                padding: '12px',
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 'var(--radius-md)',
                textTransform: 'none',
                boxShadow: 'none',
                '& .MuiSvgIcon-root': {
                  color: '#ffffff !important',
                },
                '& *': {
                  color: '#ffffff !important',
                },
                '&:hover': {
                  background: 'var(--accent-gradient)',
                  boxShadow: 'none',
                  transform: 'none',
                },
                '&:disabled': {
                  background: 'var(--accent-gradient)',
                  color: '#ffffff !important',
                  opacity: 0.7,
                },
                transition: 'all 0.3s ease-in-out',
              }}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </Box>

          {/* Sign Up Link */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'var(--text-primary)',
                mb: 1
              }}
            >
              Don't have an account?
            </Typography>
            <Link 
              to="/signup"
              style={{
                color: 'var(--neon-cyan)',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                transition: 'all 0.3s ease-in-out',
              }}
              onMouseEnter={(e) => {
                e.target.style.color = 'var(--text-primary)';
                e.target.style.textShadow = '0 0 10px var(--neon-cyan)';
              }}
              onMouseLeave={(e) => {
                e.target.style.color = 'var(--neon-cyan)';
                e.target.style.textShadow = 'none';
              }}
            >
              Create an account
            </Link>
          </Box>
        </Paper>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'var(--text-primary)',
              opacity: 0.7
            }}
          >
            © 2024 Aletheon - AI Research Platform
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default SignIn;
